import React, { useEffect, useMemo, useRef, useState } from 'react';

function bytesToMB(n) {
  return Math.round((n / (1024 * 1024)) * 10) / 10;
}

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ''));
    r.onerror = () => reject(new Error('Failed to read file'));
    r.readAsDataURL(file);
  });
}

async function downscaleToJpegBase64(file, maxSide = 1280, quality = 0.85) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('Invalid image'));
      i.src = url;
    });
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    const scale = Math.min(1, maxSide / Math.max(w, h));
    const cw = Math.max(1, Math.round(w * scale));
    const ch = Math.max(1, Math.round(h * scale));

    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, cw, ch);
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    const base64 = dataUrl.split(',')[1] || '';
    return { mimeType: 'image/jpeg', data: base64 };
  } finally {
    URL.revokeObjectURL(url);
  }
}

export default function GeminiPlayground() {
  const [messages, setMessages] = useState(() => ([
    { role: 'assistant', content: "Hi! I'm Gemini Playground. Tell me what you want to create." }
  ]));
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [model, setModel] = useState('gemini-2.5-flash');
  const [preview, setPreview] = useState(null); // { src, mimeType }
  const [attachments, setAttachments] = useState([]); // [{ mimeType, data(base64), name, size }]
  const [videoJob, setVideoJob] = useState(null); // { operationName, done, videoUri, startedAt }
  const [videoLoading, setVideoLoading] = useState(false);
  const bottomRef = useRef(null);

  const brandId = useMemo(() => {
    try {
      return (
        localStorage.getItem('currentBrandId') ||
        localStorage.getItem('selectedBrandId') ||
        sessionStorage.getItem('currentBrandId') ||
        sessionStorage.getItem('selectedBrandId') ||
        ''
      );
    } catch (_) {
      return '';
    }
  }, []);

  useEffect(() => {
    try {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    } catch (_) {}
  }, [messages.length, loading]);

  // Close preview on ESC
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setPreview(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const send = async (text, overrideModel) => {
    const trimmed = String(text || '').trim();
    const hasAttachments = (attachments || []).length > 0;
    if ((!trimmed && !hasAttachments) || loading) return;

    const userMsg = { role: 'user', content: trimmed, images: attachments.map(a => ({ mimeType: a.mimeType, data: a.data })) };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setInput('');
    setLoading(true);
    setError('');
    setAttachments([]);

    try {
      const useModel = overrideModel || model;
      const resp = await fetch('https://api.glowytics.com/api/ai/gemini/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
        body: JSON.stringify({
          model: useModel,
          temperature: 0.7,
          // IMPORTANT: Do NOT resend assistant-generated images (huge base64) in history.
          // Only send images for the latest user turn to avoid oversized payloads.
          messages: nextMessages.map((m, idx) => {
            const isUser = m?.role === 'user';
            const isLast = idx === nextMessages.length - 1;
            return {
              role: m?.role,
              content: m?.content,
              images: (isUser && isLast && Array.isArray(m?.images)) ? m.images : []
            };
          })
        })
      });

      const txt = await resp.text();
      let json;
      try { json = JSON.parse(txt); } catch { json = { message: txt }; }
      if (!resp.ok) {
        const detailsStr = (() => {
          const d = json?.details;
          if (!d) return '';
          if (typeof d === 'string') return d.slice(0, 500);
          try { return JSON.stringify(d).slice(0, 500); } catch { return ''; }
        })();
        throw new Error([json?.error, detailsStr].filter(Boolean).join(' — ') || resp.statusText);
      }

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: json?.message || '(empty)',
        images: Array.isArray(json?.images) ? json.images : []
      }]);
    } catch (e) {
      setError(e?.message || 'Failed to talk to Gemini');
    } finally {
      setLoading(false);
    }
  };

  const startVideo = async (textPrompt) => {
    const p = String(textPrompt || '').trim();
    if (!p || loading || videoLoading) return;
    if (!brandId) {
      setError('Missing brandId (X-Brand-ID)');
      return;
    }

    setError('');
    setVideoLoading(true);
    setVideoJob(null);
    try {
      // Optional: use first attachment as initial frame (best-effort)
      const firstImg = (attachments || [])[0] || null;
      const resp = await fetch('https://api.glowytics.com/api/ai/gemini/video/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
        body: JSON.stringify({
          prompt: p,
          model: 'veo-3.1-generate-preview',
          image: firstImg ? { mimeType: firstImg.mimeType, data: firstImg.data } : undefined
        })
      });

      const txt = await resp.text();
      let json;
      try { json = JSON.parse(txt); } catch { json = { message: txt }; }
      if (!resp.ok) throw new Error([json?.error, typeof json?.details === 'string' ? json.details : ''].filter(Boolean).join(' — ') || resp.statusText);

      setVideoJob({ operationName: json.operationName, done: false, videoUri: null, startedAt: Date.now() });
    } catch (e) {
      setError(e?.message || 'Failed to start video');
      setVideoJob(null);
    } finally {
      setVideoLoading(false);
    }
  };

  // Poll Veo status
  useEffect(() => {
    let timer = null;
    let cancelled = false;
    const poll = async () => {
      try {
        if (!videoJob?.operationName || videoJob?.done) return;
        const qs = new URLSearchParams();
        qs.set('operationName', videoJob.operationName);
        const resp = await fetch(`https://api.glowytics.com/api/ai/gemini/video/status?${qs.toString()}`, {
          headers: { 'X-Brand-ID': brandId },
          credentials: 'include'
        });
        const txt = await resp.text();
        let json;
        try { json = JSON.parse(txt); } catch { json = {}; }
        if (!resp.ok) throw new Error(json?.error || resp.statusText);

        if (cancelled) return;
        setVideoJob((prev) => {
          if (!prev) return prev;
          const done = !!json?.done;
          const videoUri = json?.videoUri || null;
          return { ...prev, done, videoUri };
        });
      } catch (e) {
        if (cancelled) return;
        setError(e?.message || 'Video polling failed');
      }
    };

    if (videoJob?.operationName && !videoJob?.done) {
      poll();
      timer = setInterval(poll, 10000);
    }
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [videoJob?.operationName, videoJob?.done, brandId]);

  const onPickFiles = async (filesList) => {
    try {
      const files = Array.from(filesList || []).filter(Boolean);
      if (!files.length) return;

      // Keep it small to avoid huge payloads
      const MAX_IMAGES = 4;
      const MAX_FILE_BYTES = 3_500_000; // ~3.5MB before processing
      const next = [...attachments];

      for (const f of files) {
        if (next.length >= MAX_IMAGES) break;
        if (!String(f.type || '').startsWith('image/')) continue;

        if (f.size > MAX_FILE_BYTES) {
          // Downscale aggressively for large images
          const small = await downscaleToJpegBase64(f, 1280, 0.85);
          next.push({ ...small, name: f.name, size: f.size });
          continue;
        }

        const dataUrl = await fileToDataUrl(f);
        const [prefix, b64] = dataUrl.split(',');
        const mimeType = (prefix.match(/data:([^;]+);base64/i)?.[1]) || f.type || 'image/jpeg';
        const data = b64 || '';
        if (!data) continue;
        next.push({ mimeType, data, name: f.name, size: f.size });
      }

      // Modeli otomatik DEĞİŞTİRME — kullanıcı hangi modeli seçtiyse o kalsın
      // (banana/image modeli görsel girdisini de kabul eder; text modeli zaten vision).
      setAttachments(next);
    } catch (e) {
      setError(e?.message || 'Failed to attach image');
    }
  };

  return (
    <>
      <div className="p-6">
        <div className="max-w-5xl mx-auto">
          <div className="mb-5">
            <div className="text-xl font-semibold text-gray-900">Gemini Playground</div>
            <div className="text-sm text-gray-500">Chat with Gemini and generate drafts, ideas and tasks (experimental).</div>
          </div>

          {!!error && (
            <div className="mb-4 text-xs px-3 py-2 rounded-md bg-amber-50 text-amber-700 border border-amber-200">
              {error}
            </div>
          )}

          <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
            <div className="p-4 border-b bg-gray-50 flex flex-wrap gap-2">
            <button
              className="px-3 py-2 text-xs rounded-lg border bg-white hover:bg-gray-50"
              onClick={() => { setModel('gemini-2.5-flash'); send('Generate 10 marketing campaign ideas for next month.', 'gemini-2.5-flash'); }}
              disabled={loading}
            >
              Generate ideas
            </button>
            <button
              className="px-3 py-2 text-xs rounded-lg border bg-white hover:bg-gray-50"
              onClick={() => { setModel('gemini-2.5-flash'); send('Write 3 ad copies for a Google Shopping campaign for a fashion brand in Turkish.', 'gemini-2.5-flash'); }}
              disabled={loading}
            >
              Ad copy (TR)
            </button>
            <button
              className="px-3 py-2 text-xs rounded-lg border bg-white hover:bg-gray-50"
              onClick={() => { setModel('gemini-2.5-flash-image'); send('Create a product-style image: minimal white background, soft studio lighting, centered composition. Subject: a futuristic banana with neon accents. High detail.', 'gemini-2.5-flash-image'); }}
              disabled={loading}
              title="Uses Nano Banana image model"
            >
              Generate image
            </button>
            <button
              className="px-3 py-2 text-xs rounded-lg border bg-white hover:bg-gray-50"
              onClick={() => startVideo(input.trim() || 'Cinematic 8-second video. Smooth camera motion. High realism.')}
              disabled={loading || videoLoading}
              title="Generates an 8-second Veo video from the prompt box (long-running job)"
            >
              Generate video
            </button>
            <button
              className="px-3 py-2 text-xs rounded-lg border bg-white hover:bg-gray-50"
              onClick={() => setMessages([{ role: 'assistant', content: "Hi! I'm Gemini Playground. Tell me what you want to create." }])}
              disabled={loading}
            >
              Reset chat
            </button>
            <div className="ml-auto flex items-center gap-2 text-xs text-gray-600">
              <span className="hidden sm:inline">Model</span>
              <select
                value={model}
                onChange={(e) => setModel(e.target.value)}
                disabled={loading}
                className="px-2 py-1 rounded-lg border bg-white text-xs"
              >
                <option value="gemini-2.5-flash">gemini-2.5-flash (text)</option>
                <option value="gemini-2.5-flash-image">gemini-2.5-flash-image (Nano Banana)</option>
                <option value="gemini-3-pro-image-preview">gemini-3-pro-image-preview (Nano Banana Pro)</option>
              </select>
            </div>
          </div>

          <div className="p-4 h-[560px] overflow-y-auto">
            <div className="space-y-3">
              {!!videoJob?.operationName && (
                <div className="rounded-2xl border border-gray-200 bg-white p-4">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-medium text-gray-900">Veo video job</div>
                      <div className="text-[11px] text-gray-500 break-all">{videoJob.operationName}</div>
                    </div>
                    <div className={`text-xs px-2 py-1 rounded-lg border ${videoJob.done ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                      {videoJob.done ? 'Done' : 'Generating…'}
                    </div>
                  </div>

                  {!!videoJob.videoUri && (
                    <div className="mt-3">
                      <video
                        className="w-full rounded-xl border border-gray-200"
                        controls
                        src={`https://api.glowytics.com/api/ai/gemini/video/download?${new URLSearchParams({ uri: videoJob.videoUri }).toString()}`}
                      />
                      <div className="mt-2 text-[11px] text-gray-500">
                        Note: videos are generated asynchronously (can take minutes) [[link](https://ai.google.dev/gemini-api/docs/video?example=dialogue)]
                      </div>
                    </div>
                  )}
                </div>
              )}
              {messages.map((m, idx) => {
                const isUser = m.role === 'user';
                const imgs = Array.isArray(m.images) ? m.images : [];
                return (
                  <div key={idx} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm border ${
                      isUser ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-900 border-gray-200'
                    }`}>
                      <div className="whitespace-pre-wrap">{m.content}</div>
                      {!!imgs.length && (
                        <div className="mt-3 grid grid-cols-1 gap-3">
                          {imgs.map((img, i) => (
                            <img
                              key={i}
                              alt="Generated"
                              className="rounded-xl border border-gray-200 max-w-full cursor-zoom-in hover:opacity-95"
                              src={`data:${img?.mimeType || 'image/png'};base64,${img?.data || ''}`}
                              onClick={() => {
                                const mimeType = img?.mimeType || 'image/png';
                                const data = img?.data || '';
                                if (!data) return;
                                setPreview({ mimeType, src: `data:${mimeType};base64,${data}` });
                              }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {loading && (
                <div className="flex justify-start">
                  <div className="max-w-[85%] rounded-2xl px-4 py-3 text-sm border bg-white text-gray-900 border-gray-200">
                    Thinking…
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>
          </div>

          <div className="p-4 border-t bg-white">
            {!!attachments.length && (
              <div className="mb-3 flex flex-wrap gap-2">
                {attachments.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 px-2 py-1 rounded-lg border bg-gray-50">
                    <img
                      alt="Attachment preview"
                      className="h-10 w-10 rounded-md object-cover border"
                      src={`data:${a.mimeType};base64,${a.data}`}
                      onClick={() => setPreview({ mimeType: a.mimeType, src: `data:${a.mimeType};base64,${a.data}` })}
                      style={{ cursor: 'zoom-in' }}
                    />
                    <div className="min-w-0">
                      <div className="text-[11px] text-gray-700 truncate max-w-[220px]">{a.name || 'image'}</div>
                      <div className="text-[10px] text-gray-500">{bytesToMB(a.size || 0)} MB</div>
                    </div>
                    <button
                      className="text-[11px] px-2 py-1 rounded-md border bg-white hover:bg-gray-50"
                      onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))}
                      disabled={loading}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2">
              <label className="px-3 py-2 text-sm rounded-xl border bg-white hover:bg-gray-50 cursor-pointer select-none">
                Upload
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => onPickFiles(e.target.files)}
                  disabled={loading}
                />
              </label>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send(input);
                  }
                }}
                rows={2}
                placeholder="Ask Gemini… (Enter to send, Shift+Enter for newline)"
                className="flex-1 px-3 py-2 text-sm border rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300"
              />
              <button
                className="px-4 py-2 text-sm font-medium rounded-xl bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
                onClick={() => send(input)}
                disabled={loading || (!String(input || '').trim() && !(attachments || []).length)}
              >
                Send
              </button>
              <button
                className="px-4 py-2 text-sm font-medium rounded-xl bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-50"
                onClick={() => startVideo(input)}
                disabled={videoLoading || !String(input || '').trim()}
                title="Generate a Veo video from the prompt (async)"
              >
                Video
              </button>
            </div>
            <div className="mt-2 text-[11px] text-gray-500">
              Backend endpoint: <code>/api/ai/gemini/chat</code> (requires server env var <code>GEMINI_API_KEY</code>).
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* Image lightbox preview */}
      {!!preview?.src && (
        <div
          className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="relative w-full max-w-5xl max-h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
              <div className="text-sm font-medium text-gray-800">Preview</div>
              <div className="flex items-center gap-2">
                <a
                  href={preview.src}
                  download={`gemini-image.${(preview.mimeType || '').includes('jpeg') ? 'jpg' : (preview.mimeType || '').includes('webp') ? 'webp' : 'png'}`}
                  className="px-3 py-2 text-xs rounded-lg border bg-white hover:bg-gray-50"
                >
                  Download
                </a>
                <button
                  className="px-3 py-2 text-xs rounded-lg border bg-white hover:bg-gray-50"
                  onClick={() => setPreview(null)}
                >
                  Close (Esc)
                </button>
              </div>
            </div>
            <div className="bg-black flex items-center justify-center max-h-[calc(90vh-52px)] overflow-auto">
              <img
                alt="Generated preview"
                src={preview.src}
                className="max-w-full max-h-[calc(90vh-52px)] object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}


